/**
 * 管理员订单列表
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { AdminOrderItem } from '../../types';
import Pagination from '../../components/Pagination';

const ORDER_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'pending', label: '待支付' },
  { value: 'paid', label: '已支付' },
  { value: 'cancelled', label: '已取消' },
  { value: 'expired', label: '已过期' },
];

const formatPrice = (price: number) => (price / 100).toFixed(2);

const statusTag = (s: string) => {
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    cancelled: 'bg-accent text-foreground',
    expired: 'bg-red-100 text-red-600',
    refunded: 'bg-orange-100 text-orange-700',
  };
  const label: Record<string, string> = {
    pending: '待支付',
    paid: '已支付',
    cancelled: '已取消',
    expired: '已过期',
    refunded: '已退费',
  };
  return { color: map[s] || '', text: label[s] || s };
};

const AdminOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<AdminOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchOrders = (p: number) => {
    setLoading(true);
    adminApi
      .getOrders({
        page: p,
        page_size: 20,
        status: status || undefined,
        username: username || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      })
      .then((res) => {
        const data = res.data.data;
        setOrders(data.items);
        setTotal(data.total);
        setTotalPages(Math.ceil(data.total / data.page_size));
      })
      .catch(() => setError('加载订单列表失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders(page);
  }, [page, status, username, startDate, endDate]);

  const handleSearch = () => {
    setUsername(usernameInput);
    setPage(1);
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-6">订单管理</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>
      )}

      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-border rounded-md text-sm"
        >
          {ORDER_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="搜索用户名"
          className="px-3 py-2 border border-border rounded-md text-sm w-40"
        />
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-border rounded-md text-sm"
          />
          <span className="text-gray-400 text-sm">—</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-border rounded-md text-sm"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          搜索
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">加载中...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">暂无订单</p>
        </div>
      ) : (
        <>
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-background">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">订单 ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">用户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">套餐</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">创建时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => {
                  const tag = statusTag(o.status);
                  return (
                    <tr
                      key={o.id}
                      onClick={() => navigate(`/admin/orders/${o.id}`)}
                      className="cursor-pointer hover:bg-accent"
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-foreground">#{o.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground">{o.username}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground">{o.plan_id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground">¥{formatPrice(o.amount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-xs rounded ${tag.color}`}>{tag.text}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {new Date(o.created_at).toLocaleString('zh-CN')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={20} onPageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default AdminOrders;
